import { useEffect, useState } from 'react';
import { useDealConfiguratorContext } from '../DealConfigureScreen';
import { Button } from 'components/Button';
import { FormInput } from 'components/form/FormInput';
import { FormFieldTooltip } from 'components/form/FormFieldTooltip';
import { Money, toMoney } from 'components/Money';
import { Text } from 'components/Text';
import { getOtherFeeRestrictionQuantity, getOtherFeeRestrictionSubTotal, parseRestrictionJson, toFiniteNumber } from 'helpers/otherFeesCalculation';
import { React } from 'libs/react';
function isFeeLocked(fee) {
    var _a;
    return Boolean((_a = fee.fee) === null || _a === void 0 ? void 0 : _a.locked);
}
/**
 * @component DealFeeListItem
 * @since 1.0.0
 */
export function DealFeeListItem(props) {
    var _a;
    const { deal, editable, fee, index } = props;
    const { updateOtherFees } = useDealConfiguratorContext();
    const otherFees = (_a = deal === null || deal === void 0 ? void 0 : deal.otherFees) !== null && _a !== void 0 ? _a : [];
    const locked = isFeeLocked(fee);
    const [price, setPrice] = useState(() => toFiniteNumber(fee.price).toFixed(2));
    // Price is recalculated in DealFeeList; just mirror the deal value here.
    useEffect(() => {
        setPrice(toFiniteNumber(fee.price).toFixed(2));
    }, [fee.price]);
    /**
     * @function onDescriptionChange
     * @since 1.0.o
     * @hidden
     */
    const onDescriptionChange = (description, index) => {
        if (locked) {
            return;
        }
        const tempArray = [...otherFees];
        tempArray[index] = Object.assign(Object.assign({}, tempArray[index]), { description: description });
        updateOtherFees(tempArray);
    };
    /**
     * @function onPriceChange
     * @since 1.0.o
     * @hidden
     */
    const onPriceChange = (nextPrice, index) => {
        if (locked) {
            return;
        }
        const currentFee = otherFees[index];
        const hasActuallyChanged = toFiniteNumber(nextPrice) !== toFiniteNumber(currentFee === null || currentFee === void 0 ? void 0 : currentFee.price);
        // FormInput fires onBlur on mount (and on every focus/blur cycle) even
        // without an edit, so only detach the catalog fee when the price truly
        // changed - otherwise the feeId gets silently wiped in the DB.
        if (!hasActuallyChanged) {
            return;
        }
        const tempArray = [...otherFees];
        tempArray[index] = Object.assign(Object.assign({}, tempArray[index]), { price: Number(nextPrice), 
            // Manual edit detaches the catalog fee so the price stops auto-updating.
            fee: null });
        updateOtherFees(tempArray);
    };
    /**
     * @function onItemRemove
     * @since 1.0.o
     * @hidden
     */
    const onItemRemove = (index) => {
        if (locked) {
            return;
        }
        const otherFeesTemp = [...otherFees];
        otherFeesTemp.splice(index, 1);
        updateOtherFees(otherFeesTemp);
    };
    /**
     * @function getTooltipText
     * @since 1.0.0
     * @hidden
     */
    const getTooltipText = () => {
        var _a, _b, _c;
        if (!fee.fee || !deal)
            return null;
        const restrictionExclusion = parseRestrictionJson(fee.fee.restrictionExclusion);
        const restriction = (_a = fee.fee.restriction) !== null && _a !== void 0 ? _a : 'NONE';
        const restrictionValue = restriction === 'NONE' || !fee.fee.restrictionValue
            ? []
            : (_b = parseRestrictionJson(fee.fee.restrictionValue)) !== null && _b !== void 0 ? _b : [];
        if (fee.fee.type === 'PERCENTAGE') {
            const subTotal = getOtherFeeRestrictionSubTotal(deal, restriction, restrictionValue, restrictionExclusion);
            return `${toMoney(subTotal)} × ${fee.fee.value}%`;
        }
        if (fee.fee.type === 'QUANTITY') {
            const qty = getOtherFeeRestrictionQuantity(deal, restriction, restrictionValue, restrictionExclusion);
            return `${qty} × ${toMoney((_c = fee.fee.value) !== null && _c !== void 0 ? _c : 0)}`;
        }
        return null;
    };
    const tooltipText = !editable ? getTooltipText() : null;
    return (React.createElement("div", { key: fee.id, className: "deal-fee-list-item" },
        editable ? (React.createElement("div", { className: "deal-fee-list-item-head" },
            React.createElement(FormInput, { type: "text", className: "deal-fee-list-input-description", value: fee.description, onChange: (description) => onDescriptionChange(description, index), required: true, placeholder: t('Description'), disabled: locked }),
            React.createElement(FormInput, { type: "number", className: "deal-fee-list-input-price", value: price.toString(), onBlur: (nextPrice) => onPriceChange(nextPrice, index), suffix: "$", required: true, placeholder: t('Price'), disabled: locked, onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.currentTarget.blur();
                    }
                } }))) : (React.createElement("div", { className: "deal-fee-list-item-text-head" },
            React.createElement("div", { className: "deal-fee-list-text-description-wrapper" },
                React.createElement(Text, { value: fee.fee ? fee.fee.title : fee.description, className: "deal-fee-list-text-description" }),
                tooltipText && React.createElement(FormFieldTooltip, { title: tooltipText })),
            toFiniteNumber(fee.price) != 0 ? (React.createElement(Money, { value: toFiniteNumber(fee.price), className: "deal-fee-list-text-price" })) : (React.createElement(Text, { value: t('To be determined'), className: "deal-fee-list-text-price" })))),
        React.createElement("div", { className: "deal-fee-list-item-foot" }, editable && !locked && (React.createElement(Button, { style: "picto", size: "small", picto: "trash-can", onPress: () => onItemRemove(index) })))));
}
